import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Camera, FileText, HandCoins, ShieldCheck, ArrowRight } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { ListingCard } from "@/components/ListingCard";
import { StateSelect } from "@/components/StateSelect";
import { CATEGORIES, STATES } from "@/lib/constants";
import { api } from "@/lib/api";
import { useRegion } from "@/context/AuthContext";
import { WaitlistBanner, useOpenStates } from "@/components/WaitlistBanner";
import { LaunchBanner } from "@/components/LaunchBanner";
import { AdSlot, withAds } from "@/components/AdSlot";
import { FeaturedCategories } from "@/components/FeaturedCategories";

const PAGE = 24;

const STEPS = [
  { icon: Camera, t: "Snap a few photos", d: "Add up to 8 pics straight from your phone." },
  { icon: FileText, t: "Describe it", d: "Title, category, condition. Done in a minute." },
  { icon: HandCoins, t: "Set price & suburb", d: "Buyers message you, then pay at pick-up." },
];

export default function Home() {
  const { region, setRegion } = useRegion();
  const [items, setItems] = useState([]);
  const [sold, setSold] = useState([]);
  const [stats, setStats] = useState({ by_category: {}, total: 0 });
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [featured, setFeatured] = useState([]);
  const loadMore = () => {
    setLoadingMore(true);
    api.get("/listings", { params: { state: region, limit: PAGE, hide_sold: true, skip: items.length } })
      .then((r) => { setItems((p) => [...p, ...r.data.filter((x) => !p.some((y) => y.id === x.id))]); setMore(r.data.length === PAGE); })
      .finally(() => setLoadingMore(false));
  };
  const stateName = STATES.find((s) => s.id === region)?.name;
  const openStates = useOpenStates();

  useEffect(() => {
    api.get("/listings", { params: { state: region, limit: PAGE, hide_sold: true } }).then((r) => { setItems(r.data); setMore(r.data.length === PAGE); });
    api.get("/featured-categories", { params: { state: region } }).then((r) => setFeatured(r.data));
    api.get("/recently-sold", { params: { state: region, limit: 12 } }).then((r) => setSold(r.data));
    api.get("/stats", { params: { state: region } }).then((r) => setStats(r.data));
  }, [region]);

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 pt-6 empty:hidden sm:px-6 lg:px-8"><LaunchBanner region={region} /></div>
      <section className="grain relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1727163941293-0c3dc5074bf4?w=1800&q=70" alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
        <div className="relative mx-auto max-w-7xl px-4 pb-14 pt-12 sm:px-6 lg:px-8 lg:pb-20 lg:pt-20">
          <div className="max-w-3xl space-y-6">
            <p className="rise text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">{openStates && !openStates.includes(region) ? `Coming soon to ${stateName}` : `Now live in ${stateName}`}</p>
            <h1 className="rise font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "60ms" }}>Your next bargain is here.</h1>
            <p className="rise text-base text-slate-300 sm:text-lg" style={{ animationDelay: "120ms" }}>Buy local. Sell for free. Find your next deal.</p>
            <div className="rise space-y-3" style={{ animationDelay: "180ms" }}>
              <SearchBar testId="home-search" />
              <Link to="/sell" data-testid="home-sell-btn" className="btn-lime w-full py-4 text-sm sm:text-base">
                <Plus className="h-5 w-5" /> Sell an item — free
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-3 md:hidden">
              <StateSelect value={region} onChange={setRegion} testId="home-state-select" />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto mb-8 max-w-7xl px-4 empty:hidden sm:px-6 lg:px-8"><WaitlistBanner region={region} /></section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="scrollbar-none -mx-4 flex gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-4 sm:px-0 lg:grid-cols-11">
          {CATEGORIES.map((c, i) => (
            <Link key={c.id} to={`/browse?category=${c.id}`} data-testid={`category-tile-${c.id}`} className="rise group flex min-w-[118px] flex-col items-center gap-2 rounded-2xl bg-white px-2 py-4 sm:min-w-0 text-center text-[#0f2a33] transition-transform duration-200 hover:-translate-y-1" style={{ animationDelay: `${i * 40}ms` }}>
              <c.icon className="h-6 w-6 transition-transform group-hover:scale-110" />
              <span className="text-xs font-semibold leading-tight">{c.name}</span>
              <span className="text-[10px] text-slate-400">{stats.by_category[c.id] || 0} listed</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-20 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">Fresh in {region}</p>
            <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">Latest listings</h2>
          </div>
          <Link to="/browse" data-testid="home-view-all-link" className="flex items-center gap-1 text-sm font-semibold text-[#d4f253] hover:underline">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {items.length ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
            {withAds(items, (l, i) => <ListingCard key={l.id} l={l} i={i % PAGE} />)}
          </div>
        ) : (
          <div data-testid="home-empty-state" className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-slate-400">
            No items for sale in {stateName} right now — <Link to="/sell" className="font-semibold text-[#d4f253]">list yours free!</Link>
          </div>
        )}
        {more && (
          <div className="mt-10 flex justify-center">
            <button data-testid="home-load-more-btn" onClick={loadMore} disabled={loadingMore} className="rounded-full border border-[#d4f253]/50 px-8 py-3 text-sm font-bold text-[#d4f253] transition-colors hover:bg-[#d4f253]/10 disabled:opacity-60">{loadingMore ? "Loading…" : "Load more"}</button>
          </div>
        )}
      </section>

      <FeaturedCategories groups={featured} />

      <div className="mx-auto mt-12 max-w-7xl px-4 empty:hidden sm:px-6 lg:px-8"><AdSlot testId="home-ad-slot" /></div>

      {sold.length > 0 && (
        <section data-testid="home-just-sold" className="mt-20">
          <div className="mx-auto mb-6 flex max-w-7xl items-end justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]"><span className="h-2 w-2 animate-pulse rounded-full bg-[#d4f253]" />Just sold</p>
              <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">Locals are selling fast</h2>
            </div>
            <Link to="/sell" data-testid="home-just-sold-sell-link" className="flex items-center gap-1 text-sm font-semibold text-[#d4f253] hover:underline">Sell yours free <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="scrollbar-none mx-auto flex max-w-7xl snap-x gap-4 overflow-x-auto px-4 pb-4 sm:gap-6 sm:px-6 lg:px-8">
            {sold.map((l, i) => <div key={l.id} className="w-[220px] shrink-0 snap-start sm:w-[260px]"><ListingCard l={l} i={i} /></div>)}
          </div>
        </section>
      )}

      <section className="mx-auto mt-24 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 rounded-3xl bg-[#173742] p-8 sm:p-12 lg:grid-cols-[1fr_2fr]">
          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">Sell in 3 steps</p>
            <h2 className="font-display text-2xl font-bold sm:text-3xl">Listing takes about 60 seconds.</h2>
            <p className="text-sm text-slate-300">No fees, no commission, ever.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.t} data-testid={`how-step-${i + 1}`} className="relative rounded-2xl bg-[#0f2a33] p-6">
                <span className="absolute right-5 top-4 font-display text-4xl font-extrabold text-white/10">0{i + 1}</span>
                <s.icon className="h-7 w-7 text-[#d4f253]" />
                <h3 className="mt-4 font-display font-bold">{s.t}</h3>
                <p className="mt-1 text-sm text-slate-400">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto mt-10 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 rounded-3xl border border-[#d4f253]/30 bg-[#d4f253]/5 p-8 sm:flex-row sm:items-center sm:p-10">
          <ShieldCheck className="h-12 w-12 shrink-0 text-[#d4f253]" />
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold">Pay on pick-up. We never hold your money.</h3>
            <p className="mt-1 text-sm text-slate-300">Meet the seller, inspect the item, then pay cash or PayID on the spot. No deposits, no escrow, no surprises.</p>
          </div>
          <Link to="/safety" data-testid="home-safety-link" className="rounded-xl border border-white/20 px-5 py-3 text-sm font-semibold transition-colors hover:bg-white/10">Safety tips</Link>
        </div>
      </section>
    </div>
  );
}
